import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface ShareSystemProps {
  targetId: string
  targetType: 'thread' | 'post' | 'comment'
  title: string
  description?: string
  author: string
  onShare?: (platform: string) => void
}

interface ShareOption {
  id: string
  name: string
  icon: string
  color: string
  action: (url: string, title: string, description: string) => void
}

export default function ShareSystem({
  targetId,
  targetType,
  title,
  description = '',
  author,
  onShare
}: ShareSystemProps) {
  const { user } = useAuth()
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  const baseUrl = window.location.origin
  const shareUrl = `${baseUrl}/community/${targetType}/${targetId}`
  const shareTitle = `${title} - by ${author} | Unbiased Kratom Community`
  const shareDescription = description || `Check out this ${targetType} from ${author} on Unbiased Kratom Community`

  const shareOptions: ShareOption[] = [
    {
      id: 'reddit',
      name: 'Reddit',
      icon: '📰',
      color: '#ff4500',
      action: (url, title) => {
        const redditUrl = `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
        window.open(redditUrl, '_blank', 'width=600,height=400')
      }
    },
    {
      id: 'twitter',
      name: 'Twitter',
      icon: '🐦',
      color: '#1da1f2',
      action: (url, title) => {
        const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
        window.open(twitterUrl, '_blank', 'width=600,height=400')
      }
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: '📘',
      color: '#1877f2',
      action: (url) => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
        window.open(facebookUrl, '_blank', 'width=600,height=400')
      }
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: '✈️',
      color: '#0088cc',
      action: (url, title) => {
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
        window.open(telegramUrl, '_blank', 'width=600,height=400')
      }
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: '💬',
      color: '#25d366',
      action: (url, title) => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`
        window.open(whatsappUrl, '_blank', 'width=600,height=400')
      }
    },
    {
      id: 'email',
      name: 'Email',
      icon: '📧',
      color: '#6b7280',
      action: (url, title, desc) => {
        const emailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${desc}\n\n${url}`)}`
        window.location.href = emailUrl
      }
    }
  ]

  const handleShare = async (option: ShareOption) => {
    try {
      option.action(shareUrl, shareTitle, shareDescription)
      onShare?.(option.id)
      setShowShareMenu(false)
      
      // TODO: Track sharing analytics
      console.log(`Shared ${targetType} ${targetId} to ${option.name}`)
    } catch (error) {
      console.error('Error sharing:', error)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopySuccess(true)
      setShowShareMenu(false)
      
      setTimeout(() => setCopySuccess(false), 2000)
      
      // TODO: Show toast notification
      console.log('Link copied to clipboard!')
    } catch (error) {
      console.error('Error copying link:', error)
      
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = shareUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    }
  }

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl
        })
        onShare?.('native')
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error sharing:', error)
        }
      }
    }
  }

  return (
    <div className="share-system">
      <div className="share-container">
        {/* Main Share Button */}
        <button
          onClick={() => {
            if (navigator.share) {
              handleNativeShare()
            } else {
              setShowShareMenu(!showShareMenu)
            }
          }}
          className={`share-btn ${copySuccess ? 'success' : ''}`}
          title="Share this post"
        >
          <span className="share-icon">
            {copySuccess ? '✅' : '🔗'}
          </span>
          <span className="share-label">
            {copySuccess ? 'Copied!' : 'Share'}
          </span>
        </button>

        {/* Share Menu */}
        {showShareMenu && !navigator.share && (
          <div className="share-menu">
            <div className="share-menu-header">
              <h4>Share this {targetType}</h4>
              <button
                onClick={() => setShowShareMenu(false)}
                className="close-btn"
                aria-label="Close share menu"
              >
                ×
              </button>
            </div>

            <div className="share-options">
              {/* Copy Link Option */}
              <button
                onClick={handleCopyLink}
                className="share-option copy-link"
              >
                <span className="share-option-icon">🔗</span>
                <span className="share-option-name">Copy Link</span>
              </button>

              {/* Platform Options */}
              {shareOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => handleShare(option)}
                  className="share-option"
                  style={{ '--share-color': option.color } as React.CSSProperties}
                >
                  <span className="share-option-icon">{option.icon}</span>
                  <span className="share-option-name">{option.name}</span>
                </button>
              ))}
            </div>

            {/* Share URL Preview */}
            <div className="share-preview">
              <div className="share-preview-header">
                <strong>{shareTitle}</strong>
              </div>
              {shareDescription && (
                <div className="share-preview-description">
                  {shareDescription}
                </div>
              )}
              <div className="share-preview-url">
                {shareUrl}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay to close menu */}
      {showShareMenu && (
        <div
          className="share-overlay"
          onClick={() => setShowShareMenu(false)}
        />
      )}
    </div>
  )
}